import { useProject } from '../lib/project-context';
import { useModules } from '../lib/modules-context';
import { useProjectScore } from '../lib/queries';
import { Card, CardContent } from './ui/card';
import { LineChart, TrendingUp, HardHat, ShieldAlert } from 'lucide-react';

const MODULE_ICONS = { financial: LineChart, market: TrendingUp, execution: HardHat, risk: ShieldAlert };
const MODULE_KEY_MAP = { financial: 'financial', market: 'market', execution: 'construction', risk: null }; // risk doesn't map to a module

function scoreColor(s) {
  if (s >= 75) return 'var(--success)';
  if (s >= 55) return 'var(--market-primary)';
  if (s >= 35) return 'var(--warning-color)';
  return 'var(--danger)';
}

export function ProjectScoreCard() {
  const { activeProject, activeScenario } = useProject();
  const { isActive } = useModules();
  const { data } = useProjectScore(activeProject?.id, activeScenario?.id);

  if (!activeProject || !data || data.score === null) return null;

  // Filter: only show components whose module is active (or always show risk/financial)
  const visibleComponents = data.components.filter(c => {
    if (c.status !== 'evaluated') return false; // hide unevaluated
    const modKey = MODULE_KEY_MAP[c.key];
    if (modKey === null) return true; // risk always shows if evaluated
    return isActive(modKey);
  });

  if (visibleComponents.length === 0) return null;

  const total = data.score;
  const color = scoreColor(total);
  const circ = 2 * Math.PI * 38;
  const offset = circ - (total / 100) * circ;

  return (
    <Card className="rounded-xl border-border/50 shadow-sm mb-6" data-testid="project-score-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-5">
          {/* Gauge */}
          <div className="relative shrink-0" style={{ width: 88, height: 88 }}>
            <svg width="88" height="88" viewBox="0 0 88 88">
              <circle cx="44" cy="44" r="38" fill="none" stroke="var(--bg-hover)" strokeWidth="5" />
              <circle cx="44" cy="44" r="38" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={offset}
                transform="rotate(-90 44 44)" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-display font-bold tabular-nums" style={{ color }}>{total}</span>
              <span className="text-[7px] font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>score</span>
            </div>
          </div>

          {/* Breakdown — only active modules */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Project Score</span>
              <span className="text-sm font-bold" style={{ color }}>{data.label}</span>
            </div>
            <div className="space-y-2 mt-2">
              {visibleComponents.map(c => {
                const Icon = MODULE_ICONS[c.key] || LineChart;
                return (
                  <div key={c.key} className="flex items-center gap-2">
                    <Icon className="w-3 h-3 shrink-0" style={{ color: c.color }} />
                    <span className="text-[10px] font-medium w-[120px] truncate" style={{ color: 'var(--text-secondary)' }}>{c.label}</span>
                    <div className="flex-1 h-1 rounded-full" style={{ background: 'var(--bg-hover)' }}>
                      <div className="h-full rounded-full" style={{ width: `${c.score}%`, background: scoreColor(c.score), transition: 'width 0.6s ease' }} />
                    </div>
                    <span className="text-[10px] font-semibold tabular-nums w-6 text-right" style={{ color: scoreColor(c.score) }}>{c.score}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
